// Weapon fire for presentation (GDD §8): the sim advances Weapon.shots once per attack; this system turns every advance
// seen in view into an AttackView for the VFX layer, with what the shooter and its target are and their render poses. A weapon seen
// for the first time (spawned into view, or back out of the fog) only sets its baseline. It runs after interpolation,
// so poses are this frame's. The batch and its views are reused frame to frame, so nothing allocates once the view
// pool has grown to the busiest frame; listeners must not keep them.

import { Matcher, type Component, type ComponentType, type Entity } from '@esengine/ecs-framework'
import { BUILDING_KEYS, TRANSPORT_MODE_KEYS, UNIT_KEYS } from '@taxwar/content'
import {
  Building,
  CityNode,
  InvariantViolation,
  NEUTRAL_OWNER,
  NO_SIM_ID,
  Owner,
  SHOT_COUNTER_WRAP,
  SimIdentity,
  TransportHull,
  UnitKind,
  Weapon,
  type BuildingTypeId,
  type OwnerValue,
  type SimId,
  type TransportModeCode,
  type UnitTypeId,
} from '@taxwar/net'
import type { AttackView, Combatant } from './contracts'
import { isEmbarkedCargo } from './cargo'
import { PoseTrack, PresentationOrder, PresentationSystem } from './presentation'

const SINGLE_BODY = 1
const STRUCTURE_HEADING = 0

/** Combatant kinds are shared constants, so a view never allocates one. */
const UNIT_KINDS: readonly Combatant[] = UNIT_KEYS.map((_, typeId) => Object.freeze({ kind: 'unit', unitType: typeId as UnitTypeId }))
const BUILDING_KINDS: readonly Combatant[] = BUILDING_KEYS.map((_, typeId) => Object.freeze({ kind: 'building', buildingType: typeId as BuildingTypeId }))
const CITY_KIND: Combatant = Object.freeze({ kind: 'city' })
const TRANSPORT_KINDS: readonly Combatant[] = TRANSPORT_MODE_KEYS.map((_, mode) => Object.freeze({ kind: 'transport', mode: mode as TransportModeCode }))

class MutableAttack implements AttackView {
  shooter = NO_SIM_ID
  owner: OwnerValue = NEUTRAL_OWNER
  shooterKind: Combatant = CITY_KIND
  targetKind: Combatant = CITY_KIND
  fromLat = 0
  fromLon = 0
  heading = 0
  toLat = 0
  toLon = 0
  bodies = SINGLE_BODY
}

/** A mirror entity by SimId (MirrorWorld.find); undefined when it is not in view. */
export type EntityLookup = (simId: SimId) => Entity | undefined

function kindOf(entity: Entity): Combatant {
  for (let i = 0; i < KIND_RESOLVERS.length; i++) {
    const resolver = KIND_RESOLVERS[i] as KindResolver
    if (entity.getComponent(resolver.component) !== null) return resolver.resolve(entity)
  }
  throw new InvariantViolation(`SimId ${(entity.getComponent(SimIdentity) as SimIdentity).simId} fights but is no unit, city, building or transport`)
}

function lookup(kinds: readonly Combatant[], typeId: number, what: string): Combatant {
  const kind = kinds[typeId]
  if (kind === undefined) throw new InvariantViolation(`unknown ${what} id ${typeId}`)
  return kind
}

/** One entry per combatant component kind: the order decides the kind of an entity that carries several. */
interface KindResolver {
  readonly component: ComponentType<Component>
  readonly resolve: (entity: Entity) => Combatant
}

const KIND_RESOLVERS: readonly KindResolver[] = [
  { component: UnitKind, resolve: (entity) => lookup(UNIT_KINDS, (entity.getComponent(UnitKind) as UnitKind).typeId, 'unit type') },
  { component: CityNode, resolve: () => CITY_KIND },
  { component: Building, resolve: (entity) => lookup(BUILDING_KINDS, (entity.getComponent(Building) as Building).typeId, 'building type') },
  // A craft has no weapon (attackStyleOf rejects one that fired); it is a target, and the registry makes that decodable.
  { component: TransportHull, resolve: (entity) => lookup(TRANSPORT_KINDS, (entity.getComponent(TransportHull) as TransportHull).mode, 'transport mode') },
]

export class AttackDetectionSystem extends PresentationSystem {
  /** Shots per weapon SimId: as of the last frame, and being filled for this frame (swapped each frame). */
  private last = new Map<number, number>()
  private current = new Map<number, number>()
  private readonly pool: MutableAttack[] = []
  private readonly batch: AttackView[] = []

  constructor(
    private readonly find: EntityLookup,
    private readonly emit: (attacks: readonly AttackView[]) => void,
  ) {
    super(Matcher.all(Weapon, Owner, SimIdentity, PoseTrack), PresentationOrder.attacks)
  }

  /** Every weapon's next sighting only sets its baseline again (a resync respawns them all into view). */
  forgetBaselines(): void {
    this.last.clear()
    this.current.clear()
  }

  protected override present(entities: readonly Entity[]): void {
    const last = this.last
    const current = this.current
    current.clear()
    this.batch.length = 0
    for (let i = 0; i < entities.length; i++) {
      const entity = entities[i] as Entity
      const weapon = entity.getComponent(Weapon) as Weapon
      const simId = (entity.getComponent(SimIdentity) as SimIdentity).simId
      current.set(simId, weapon.shots)
      // Cargo rides a hull: it neither fires nor is a target. Its counter still advances (above) so a unit that
      // unloads later can never replay shots that accrued while it was aboard.
      if (isEmbarkedCargo(entity)) continue
      const before = last.get(simId)
      if (before === undefined || before === weapon.shots) continue
      if (weapon.lockedTarget === NO_SIM_ID) continue
      const target = this.find(weapon.lockedTarget as SimId)
      if (target === undefined || target.getComponent(PoseTrack) === null || isEmbarkedCargo(target)) continue
      const volleys = (weapon.shots - before + SHOT_COUNTER_WRAP) % SHOT_COUNTER_WRAP
      for (let v = 0; v < volleys; v++) this.batch.push(this.fill(entity, simId as SimId, target))
    }
    this.last = current
    this.current = last
    if (this.batch.length > 0) this.emit(this.batch)
  }

  private fill(entity: Entity, simId: SimId, target: Entity): AttackView {
    const index = this.batch.length
    let view = this.pool[index]
    if (view === undefined) {
      view = new MutableAttack()
      this.pool.push(view)
    }
    const pose = entity.getComponent(PoseTrack) as PoseTrack
    const unit = entity.getComponent(UnitKind)
    view.shooter = simId
    view.owner = (entity.getComponent(Owner) as Owner).player as OwnerValue
    const targetPose = target.getComponent(PoseTrack) as PoseTrack
    view.shooterKind = kindOf(entity)
    view.targetKind = kindOf(target)
    view.fromLat = pose.lat
    view.fromLon = pose.lon
    view.heading = unit === null ? STRUCTURE_HEADING : pose.heading
    view.toLat = targetPose.lat
    view.toLon = targetPose.lon
    view.bodies = unit === null ? SINGLE_BODY : unit.bodies
    return view
  }
}
